import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import EventCard from './EventCard';
import { fetchEvents } from '../utils/api';
import { getStylesFromUrl } from '../utils/styleConfig';

const Container = styled.div`
  background: ${props => props.styles.backgroundColor || '#ffffff'};
  font-family: ${props => props.styles.fontFamily || 'system-ui'};
  width: 100%;
  height: 100vh;  // Fill available height
  overflow: hidden; // Prevent scrolling
`;

const CarouselContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%; // Fill parent height
`;

const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.3s ease;
  gap: ${props => props.gap || '16px'};
  transform: translateX(-${props => props.offset}px);
`;

const CarouselItem = styled.div`
  flex: 0 0 ${props => props.width || '300px'};
  max-width: ${props => props.width || '300px'};
  min-width: ${props => props.width || '300px'};
  
  @media (max-width: 768px) {
    flex: 0 0 ${props => props.mobileWidth || '85vw'};
    max-width: ${props => props.mobileWidth || '85vw'};
    min-width: ${props => props.mobileWidth || '85vw'};
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: ${props => props.styles.backgroundColor || '#ffffff'};
  border: 1px solid ${props => props.styles.borderColor || '#e5e7eb'};
  padding: 8px;
  color: ${props => props.styles.textColor || '#000000'};
  cursor: pointer;
  border-radius: ${props => props.styles.borderRadius || '8px'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  &.prev {
    left: 8px;
  }
  
  &.next {
    right: 8px;
  }
`;

const EventsCarousel = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [offset, setOffset] = useState(0);
  const styles = useMemo(() => getStylesFromUrl(), []);

  const params = new URLSearchParams(window.location.search);
  const organization = params.get('organization');
  const maxEvents = parseInt(params.get('maxEvents') || '10', 10);
  const cardWidth = parseInt(params.get('cardWidth') || '300', 10);
  const cardGap = parseInt(params.get('cardGap') || '16', 10);
  const mobileCardWidth = params.get('mobileCardWidth') || '85vw';

  // Load events
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        const eventData = await fetchEvents(organization);
        setEvents(eventData.slice(0, maxEvents));
      } catch (err) {
        setError('Failed to load events');
        console.error('Error loading events:', err);
      } finally {
        setLoading(false);
      }
    };

    if (organization) {
      loadEvents();
    } else {
      setError('Organization parameter is required');
      setLoading(false);
    }
  }, [organization, maxEvents]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setOffset((currentIndex - 1) * (cardWidth + cardGap));
    } else {
      setCurrentIndex(events.length - 1);
      setOffset((events.length - 1) * (cardWidth + cardGap));
    }
  };

  const handleNext = () => {
    if (currentIndex < events.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setOffset((currentIndex + 1) * (cardWidth + cardGap));
    } else {
      setCurrentIndex(0);
      setOffset(0);
    }
  };

  if (loading) {
    return <div>Loading events...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Container styles={styles}>
      <CarouselContainer>
        <CarouselTrack gap={`${cardGap}px`} offset={offset}>
          {events.map((event, index) => (
            <CarouselItem
              key={index}
              width={`${cardWidth}px`}
              mobileWidth={mobileCardWidth}
            >
              <EventCard
                title={event.name}
                date={event.frequency === "Once"
                  ? `${event.dates[0].split('T')[0]}T${event.start_time}`
                  : `${event.dates[0].split('T')[0]}T${event.start_time}`}
                image={event.img}
                location={event.location}
                href={`https://ummahfy.com/event/${event.event_id}`}
                styles={styles}
              />
            </CarouselItem>
          ))}
        </CarouselTrack>

        {events.length > 1 && (
          <>
            <NavigationButton onClick={handlePrevious} className="prev" styles={styles}>
              <ChevronLeft />
            </NavigationButton>
            <NavigationButton onClick={handleNext} className="next" styles={styles}>
              <ChevronRight />
            </NavigationButton>
          </>
        )}
      </CarouselContainer>
    </Container>
  );
};

export default EventsCarousel;